import { IonContent, IonPage } from '@ionic/react';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router';
import { IState, IUserInfo } from 'appRedux/createStore';
import { INotificationChannels } from 'services/player';
import DefaultBehaviorToast from './DefaultBehaviorToast';
import { SettingRow, SettingsToggle, formatPhone } from './SettingRow';
import SettingsHeader from './SettingsHeader';
import { NOTIFICATION_GROUPS, NOTIFICATION_KEYS } from './notificationOptions';
import { isSameSettingValue } from './settingsOptions';
import { anyVisible } from './settingsVisibility';
import { useUserSettings } from './useUserSettings';

// Notification Settings, opened from Settings > Edit Notifications. "All ON/OFF"
// is the same switch as on the Settings page; while it's off the rest of the
// page keeps its values but can't be changed.
const NotificationSettings: React.FC = () => {
  const history = useHistory();
  const { phone, emailAddress } = useSelector<IState>(
    (state) => state.app.persistedState.userInfo
  ) as IUserInfo;
  const { settings, defaults, updateSetting, resetSettings, visibility } = useUserSettings({
    refresh: true,
  });
  const [showDefaultToast, setShowDefaultToast] = useState(false);
  const closeDefaultToast = useCallback(() => setShowDefaultToast(false), []);

  const isLocked = !settings?.notificationsEnabled;

  const channels: { field: keyof INotificationChannels; label: string; subtext?: string }[] = [
    { field: 'inApp', label: 'In app Notifications', subtext: 'Notify me directly...' },
    { field: 'push', label: 'Push Notifications', subtext: 'Notify me directly...' },
    { field: 'email', label: 'Email', subtext: emailAddress },
    { field: 'sms', label: 'SMS', subtext: formatPhone(phone) },
  ];

  const shown = visibility.notificationSettings;
  const visibleChannels = channels.filter(({ field }) => shown.channels[field]);
  const visibleGroups = NOTIFICATION_GROUPS.filter(
    ({ visibilityKey }) => shown.groups[visibilityKey]
  );
  const showTopicsCard = anyVisible(shown.topics) || visibleGroups.length > 0;

  const setChannel = (field: keyof INotificationChannels, checked: boolean) => {
    if (settings) {
      updateSetting('notificationChannels', { ...settings.notificationChannels, [field]: checked });
    }
  };

  const handleReset = () => {
    if (
      settings &&
      defaults &&
      NOTIFICATION_KEYS.every((key) => isSameSettingValue(settings[key], defaults[key]))
    ) {
      setShowDefaultToast(true);
      return;
    }
    resetSettings(NOTIFICATION_KEYS);
  };

  if (!visibility.settings.notifications.editNotifications) {
    return <Redirect to="/settings" />;
  }

  return (
    <IonPage className="settings-page">
      <SettingsHeader />
      <IonContent className="settings-content">
        <section className="settings-group settings-group--stack">
          <h2 className="settings-group__title">Notification Settings</h2>

          {visibility.settings.notifications.allOnOff && (
            <div className="settings-card">
              <SettingRow label="All ON/OFF">
                <SettingsToggle
                  checked={!!settings?.notificationsEnabled}
                  label="All notifications"
                  disabled={!settings}
                  onChange={(checked) => updateSetting('notificationsEnabled', checked)}
                />
              </SettingRow>
            </div>
          )}

          {visibleChannels.length > 0 && (
            <div className={`settings-card${isLocked ? ' settings-card--locked' : ''}`}>
              {visibleChannels.map(({ field, label, subtext }) => (
                <SettingRow key={field} label={label} subtext={subtext}>
                  <SettingsToggle
                    checked={!!settings?.notificationChannels[field]}
                    label={label}
                    disabled={isLocked}
                    onChange={(checked) => setChannel(field, checked)}
                  />
                </SettingRow>
              ))}
            </div>
          )}

          {showTopicsCard && (
            <div
              className={`settings-card settings-card--spaced${
                isLocked ? ' settings-card--locked' : ''
              }`}
            >
              {shown.topics.events && (
                <SettingRow label="Events">
                  <SettingsToggle
                    checked={!!settings?.eventNotifications}
                    label="Events"
                    disabled={isLocked}
                    onChange={(checked) => updateSetting('eventNotifications', checked)}
                  />
                </SettingRow>
              )}
              {shown.topics.leaderboards && (
                <SettingRow label="Leaderboards">
                  <SettingsToggle
                    checked={!!settings?.leaderboardNotifications}
                    label="Leaderboards"
                    disabled={isLocked}
                    onChange={(checked) => updateSetting('leaderboardNotifications', checked)}
                  />
                </SettingRow>
              )}
              {visibleGroups.map((group) => (
                <SettingRow
                  key={group.key}
                  label={group.label}
                  value={settings ? group.summary(settings[group.key]) : ''}
                  disabled={isLocked}
                  onClick={() => history.push(`/settings/notifications/${group.slug}`)}
                />
              ))}
            </div>
          )}
        </section>

        {shown.resetToDefault && (
          <button
            className="settings-card settings-action"
            disabled={!settings}
            onClick={handleReset}
          >
            <span className="settings-action__label">Reset to Default</span>
          </button>
        )}
      </IonContent>

      {showDefaultToast && <DefaultBehaviorToast onClose={closeDefaultToast} />}
    </IonPage>
  );
};

export default NotificationSettings;
